import React, { useState, useRef, useEffect } from "react";

// Modules

// App
import { presenterNotesEnabled, presenterAudioEnabled } from "../../../config";
import { useSlideContext } from "./slide/edit/slideContext";
import { useWindowSize } from "../../../partials/windowSize";
import AnalyseSlideWithAi from "../../../partials/ai/AnalyseSlide";

// UI components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  Button,
  FormControlLabel,
  Checkbox,
  Tooltip,
  IconButton,
  Box,
  Typography,
  TextField,
  SvgIcon,
} from "@mui/material";
import {
  PlayArrow,
  Settings,
  KeyboardBackspace,
  BorderClear,
  BorderAll,
} from "@mui/icons-material";
import { ReactComponent as MusicNoteIcon } from "../../../assets/music-notes.svg";
import UnhideIcon from "@mui/icons-material/VisibilityOutlined";
import HideIcon from "@mui/icons-material/VisibilityOffOutlined";
import NotesIcon from "@mui/icons-material/TextSnippetOutlined";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BackgroundIcon from "@mui/icons-material/WallpaperOutlined";
import Grid4x4Icon from "@mui/icons-material/Grid4x4";
import ToolbarBtn from "./toolbarBtn";

export default function Toolbar(props) {
  const backTooltipTarget = useRef(null);
  const viewTooltipTarget = useRef(null);
  const settingsTooltipTarget = useRef(null);
  const logoTooltipTarget = useRef(null);
  const backgroundTooltipTarget = useRef(null);
  const transitionTooltipTarget = useRef(null);
  const deleteTooltipTarget = useRef(null);
  const notesTooltipTarget = useRef(null);
  const audioTooltipTarget = useRef(null);
  const presentationAudioTooltipTarget = useRef(null);

  const [index, setIndex] = useState(0);
  const [slide, setSlide] = useState(null);
  const [isReadOnly, setReadOnly] = useState(false);
  const [isCorrupt, setCorrupt] = useState(false);

  const permissions = props.permissions;

  const {
    activeSlideTitle,
    activeSlideLayout,
    triggerToggleGrid,
    triggerModifySlideTitle,
    hasSlideAudio,
    triggerToggleSelect,
    triggerGroup,
    triggerUngroup,
    selectedBlockCount,
    selectedGroupCount,
    selectModeStatus,
    blockOutlines,
    triggerBlockOutlines,
  } = useSlideContext();

  const { width } = useWindowSize();

  useEffect(() => {
    setIndex(props.indexh);
  }, [props.indexh]);

  useEffect(() => {
    if (props.presentation && props.presentation?.slides?.length > 0) {
      props.presentation.slides.forEach((slide, slideIndex) => {
        if (index === slideIndex) {
          setSlide(slide);

          if (slide.initial_presentation !== props.presentation.id) {
            setReadOnly(true);
          } else {
            setReadOnly(false);
          }
        }
      });
    }
  }, [index, props.presentation, permissions, props.userAuth]);

  useEffect(() => {
    if (slide?.id) {
      if (props.corruptSlides.length) {
        if (props.corruptSlides.includes(slide.id)) {
          setCorrupt(true);
        } else {
          setCorrupt(false);
        }
      }
    }
  }, [slide, props.corruptSlides]);

  return (
    <div className="ps-toolbar">
      <Container fluid>
        <Row className="align-items-center justify-content-between toolbar-row first-row">
          <Col xs={"auto"} lg={6} className="ls">
            <Row className="align-items-center">
              <Col xs="auto">
                <Tooltip
                  id="back-tooltip"
                  title="Back to presentations"
                  placement="bottom"
                >
                  <IconButton
                    ref={backTooltipTarget}
                    variant="circular"
                    owner="admin"
                    className="d-flex align-items-center"
                    onClick={() => {
                      props.goBack();
                    }}
                    color="white"
                  >
                    <KeyboardBackspace />
                  </IconButton>
                </Tooltip>
              </Col>

              {!props.isError && (
                <Col xs="auto">
                  <Tooltip
                    id="settings"
                    title="Presentation settings"
                    placement="bottom"
                  >
                    <IconButton
                      ref={settingsTooltipTarget}
                      variant="circular"
                      owner="admin"
                      className="d-flex align-items-center"
                      onClick={() => {
                        props.onPresentationModal();
                      }}
                      color="white"
                    >
                      <Settings />
                    </IconButton>
                  </Tooltip>
                </Col>
              )}

              {!props.isError && (
                <>
                  {presenterAudioEnabled && (
                    <Col xs="auto">
                      <Tooltip
                        id="presentation-audio-settings"
                        title="Presentation audio"
                        placement="bottom"
                      >
                        <IconButton
                          ref={presentationAudioTooltipTarget}
                          id="presentation-audio-settings-button"
                          variant="circular"
                          owner="admin"
                          className={`d-flex align-items-center ${props.hasPresentationAudio ? "has-presentation-audio" : ""}`}
                          onClick={() => {
                            props.onPresentationAudioModal();
                          }}
                          color="white"
                        >
                          <MusicNoteIcon
                            style={{
                              height: "20px",
                              width: "auto",
                              padding: "1px",
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Col>
                  )}
                </>
              )}

              <Col xs="auto">
                {props?.presentation?.slides?.length > 0 && (
                  <div className="d-flex align-items-center">
                    {width >= 992 ? (
                      <Button
                        variant="circular"
                        owner="admin"
                        ref={viewTooltipTarget}
                        className="preview-btn d-flex align-items-center"
                        // className='d-flex align-items-center'
                        onClick={() => {
                          window.open(
                            `/presentations/preview/${props?.presentation?.id}`,
                            "_blank",
                          );
                        }}
                        startIcon={<PlayArrow fontSize={"small"} />}
                      >
                        <Typography variant="subtitle2" owner="admin">
                          Preview
                        </Typography>
                      </Button>
                    ) : (
                      <Tooltip
                        id="presentation-preview"
                        title="Preview presentation"
                        placement="bottom"
                      >
                        <IconButton
                          ref={presentationAudioTooltipTarget}
                          variant="circular"
                          owner="admin"
                          className="d-flex align-items-center"
                          onClick={() => {
                            window.open(
                              `/presentations/preview/${props?.presentation?.id}`,
                              "_blank",
                            );
                          }}
                          color="white"
                        >
                          <PlayArrow
                            style={{
                              height: "20px",
                              width: "auto",
                              padding: "1px",
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Col>
          <Col xs={"auto"} lg={6} className="rs">
            {!props.isError && (
              <Row className="align-items-center justify-content-end">
                <Col xs="auto" className="d-none d-lg-flex">
                  <Typography
                    owner="admin"
                    variant="subtitle2"
                    textTransform={"none"}
                    color={"adminPrimary.main"}
                  >
                    Slide title
                  </Typography>
                </Col>
                <Col xs={"auto"} className="pe-0">
                  <Tooltip
                    id="slide-title-input-tooltip"
                    title={
                      isReadOnly
                        ? "This slide is read only. Slide title can not be changed"
                        : ""
                    }
                    placement="bottom"
                  >
                    <TextField
                      className="toolbar-slide-title-input"
                      InputLabelProps={{ shrink: width >= 992 ? false : true }}
                      InputProps={{ placeholder: "Add a name to your slide" }}
                      disabled={isReadOnly}
                      value={activeSlideTitle}
                      variant="outlined"
                      label={width >= 992 ? "" : "Slide title"}
                      onChange={(e) => {
                        triggerModifySlideTitle(e.target.value);
                        // setActiveSlideTitle(e.target.value);
                        // setModifiedSlideTitle(true);
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "3px 5px",
                          fontSize: "0.8rem",
                          background: "#ffffff",
                        },
                      }}
                    ></TextField>
                  </Tooltip>
                </Col>
              </Row>
            )}
          </Col>
        </Row>

        {!isCorrupt && (
          <>
            <Row className="align-items-center toolbar-row second-row">
              <Col sm={12} md={4} xxl={3} className="ls">
                <Tooltip
                  id="presentation-title-tooltip"
                  title={props?.presentation?.title}
                  placement="bottom"
                >
                  <Typography
                    className="ps-title"
                    variant="subtitle2"
                    owner="admin"
                    color="adminText.dark"
                    sx={{
                      width: `${
                        width >= 1400
                          ? (width - 260) * 0.25
                          : (width - 260) * 0.3333
                      }px`,
                    }}
                  >
                    #{props?.presentation?.id}: {props?.presentation?.title}
                  </Typography>
                </Tooltip>
              </Col>
              {!props.isError && (
                <Col sm={12} md={8} xxl={9} className="rs">
                  <Row className="align-items-center justify-content-end">
                    {!isReadOnly && (
                      <>
                        <Col xs="auto">
                          <AnalyseSlideWithAi
                            slide={slide}
                            presentation={props?.presentation}
                            slideLayout={activeSlideLayout}
                            csrf_token={props.csrf_token}
                            refreshPresentation={props.refreshPresentation}
                          />
                        </Col>

                        {activeSlideLayout === "custom" && (
                          <>
                            <ToolbarBtn
                              width={width}
                              tooltipId="slide-group-tooltip"
                              tooltipTitle="Group selected blocks"
                              btnDisabled={selectedBlockCount <= 1}
                              onClick={() => triggerGroup()}
                              icon={
                                <Grid4x4Icon
                                  sx={{ width: "20px", height: "20px" }}
                                />
                              }
                              btnLabel="Group"
                            />
                            <ToolbarBtn
                              width={width}
                              tooltipId="slide-ungroup-tooltip"
                              tooltipTitle="Ungroup selected blocks"
                              btnDisabled={selectedGroupCount === 0}
                              onClick={() => triggerUngroup()}
                              icon={
                                <Grid4x4Icon
                                  sx={{ width: "20px", height: "20px" }}
                                />
                              }
                              btnLabel="Ungroup"
                            />
                            <ToolbarBtn
                              width={width}
                              tooltipId="slide-select-tooltip"
                              tooltipTitle="Multiple block selection"
                              btnClassName={
                                selectModeStatus ? "select-mode-on" : ""
                              }
                              btnId="block-selection-toggle"
                              onClick={() => triggerToggleSelect()}
                              icon={
                                <Grid4x4Icon
                                  sx={{ width: "20px", height: "20px" }}
                                />
                              }
                              btnLabel="Select"
                            />
                            <ToolbarBtn
                              width={width}
                              tooltipId="slide-grid-tooltip"
                              tooltipTitle="Toggle grid"
                              onClick={() => triggerToggleGrid()}
                              icon={
                                <Grid4x4Icon
                                  sx={{ width: "20px", height: "20px" }}
                                />
                              }
                              btnLabel="Grid"
                            />
                          </>
                        )}

                        <ToolbarBtn
                          width={width}
                          tooltipId="slide-outline-tooltip"
                          tooltipTitle={`${blockOutlines ? "Hide" : "Show"} block outlines`}
                          onClick={() => triggerBlockOutlines()}
                          icon={
                            <Grid4x4Icon
                              sx={{ width: "20px", height: "20px" }}
                            />
                          }
                          btnLabel="Outlines"
                        />
                        <Col xs="auto">
                          <Box className="bx">
                            <Tooltip
                              id="slide-settings-tooltip"
                              title="Slide settings"
                              placement="bottom"
                            >
                              <Button
                                variant="toolbar-btn"
                                sub_variant="primary"
                                owner="admin"
                                onClick={() => {
                                  props.onSlideSettings();
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  owner="admin"
                                  className="btn-lh"
                                >
                                  {width <= 1200 ? (
                                    <SettingsOutlinedIcon
                                      sx={{ width: "20px", height: "20px" }}
                                    />
                                  ) : (
                                    "Settings"
                                  )}
                                </Typography>
                              </Button>
                            </Tooltip>
                          </Box>
                        </Col>

                        <Col xs="auto">
                          <Box className="bx">
                            <Tooltip
                              id="background-tooltip"
                              title="Slide background"
                              placement="bottom"
                            >
                              <Button
                                ref={backgroundTooltipTarget}
                                variant="toolbar-btn"
                                sub_variant="primary"
                                owner="admin"
                                onClick={() => {
                                  props.onBackgroundSettings();
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  owner="admin"
                                  className="btn-lh"
                                >
                                  {width <= 1200 ? (
                                    <BackgroundIcon
                                      sx={{ width: "20px", height: "20px" }}
                                    />
                                  ) : (
                                    "Background"
                                  )}
                                </Typography>
                              </Button>
                            </Tooltip>
                          </Box>
                        </Col>

                        <Col xs="auto">
                          <Box className="bx">
                            <Tooltip
                              id="logo-tooltip"
                              title="Slide logo"
                              placement="bottom"
                            >
                              <Button
                                ref={logoTooltipTarget}
                                variant="toolbar-btn"
                                sub_variant="primary"
                                owner="admin"
                                onClick={() => {
                                  props.onLogoSettings();
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  owner="admin"
                                  className="btn-lh"
                                >
                                  {width <= 1200 ? (
                                    <SvgIcon
                                      sx={{
                                        width: "20px",
                                        height: "20px",
                                        fill: "currentcolor",
                                      }}
                                    >
                                      <svg
                                        width="1200pt"
                                        height="1200pt"
                                        version="1.1"
                                        viewBox="0 0 1200 1200"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <g>
                                          <path d="m1136.4 537.6-87.602-58.801 46.801-94.801c10.801-22.801 9.6016-48-2.3984-69.602-12-21.602-34.801-36-60-37.199l-105.6-7.1992-7.1992-104.4c-1.1992-25.199-15.602-46.801-37.199-60-21.602-12-48-13.199-69.602-2.3984l-93.598 46.797-58.801-87.602c-27.602-40.801-96-42-123.6 0l-57.598 87.602-94.801-46.801c-22.801-10.801-48-9.6016-69.602 2.3984-21.602 12-36 34.801-37.199 60l-7.1992 105.6-104.4 7.1992c-25.199 1.1992-46.801 15.602-60 37.199-12 21.602-13.199 48-2.3984 70.801l46.801 94.801-87.602 58.801c-20.398 14.398-33.602 37.199-33.602 62.398 0 25.199 12 48 33.602 62.398l87.602 58.801-46.801 94.801c-10.801 22.801-9.6016 49.199 2.3984 69.602 12 21.602 34.801 34.801 60 37.199l105.6 7.1992 7.1992 104.4c1.1992 25.199 15.602 46.801 37.199 60 21.602 12 48 13.199 69.602 2.3984l94.801-46.801 58.801 87.602c14.398 20.398 37.199 33.602 62.398 33.602 25.199 0 48-12 62.398-33.602l58.801-87.602 94.801 46.801c22.801 10.801 48 9.6016 69.602-2.3984 21.602-12 36-34.801 37.199-60l7.1992-105.6 105.6-7.1992c25.199-1.1992 46.801-15.602 60-37.199 12-21.602 13.199-48 2.3984-69.602l-50.402-97.195 87.602-58.801c20.398-14.398 33.602-37.199 33.602-62.398-1.2031-25.199-13.203-48-34.805-61.199zm-148.8 114c-31.199 20.398-42 61.199-25.199 94.801l42 85.199-94.801 6c-37.199 2.3984-67.199 32.398-69.602 69.602l-6 94.801-85.199-42c-33.602-16.801-74.398-6-94.801 25.199l-54 80.398-52.801-79.199c-14.398-21.602-38.398-33.602-62.398-33.602-10.801 0-22.801 2.3984-32.398 7.1992l-85.199 42-7.2031-94.801c-2.3984-37.199-32.398-67.199-69.602-69.602l-94.801-6 42-85.199c16.801-33.602 6-74.398-25.199-94.801l-79.199-52.801 79.199-52.801c31.199-20.398 42-61.199 25.199-94.801l-42-85.199 94.801-6c37.199-2.3984 67.199-32.398 69.602-69.602l6-94.801 85.199 42c33.602 16.801 74.398 6 94.801-25.199l52.801-79.199 52.801 79.199c20.398 31.199 61.199 42 94.801 25.199l85.199-42 6 94.801c2.3984 37.199 32.398 67.199 69.602 69.602l94.801 6-42 85.199c-16.801 33.602-6 74.398 25.199 94.801l79.199 52.801z" />
                                          <path d="m720 548.4c-9.6016-14.398-33.602-14.398-43.199 0l-40.801 60-76.801-111.6c-9.6016-14.398-33.602-14.398-43.199 0l-136.8 199.2c-6 8.3984-6 18-1.1992 27.602 4.8008 8.3984 13.199 14.398 22.801 14.398h397.2c9.6016 0 19.199-4.8008 22.801-14.398 4.8008-8.3984 3.6016-19.199-1.1992-27.602z" />
                                          <path d="m733.2 423.6c0 31.148-25.25 56.398-56.398 56.398-31.148 0-56.402-25.25-56.402-56.398 0-31.152 25.254-56.402 56.402-56.402 31.148 0 56.398 25.25 56.398 56.402" />
                                        </g>
                                      </svg>
                                    </SvgIcon>
                                  ) : (
                                    "Logo"
                                  )}
                                </Typography>
                              </Button>
                            </Tooltip>
                          </Box>
                        </Col>

                        <Col xs="auto">
                          <Box className="bx">
                            <Tooltip
                              id="transition-tooltip"
                              title="Slide transition"
                              placement="bottom"
                            >
                              <Button
                                ref={transitionTooltipTarget}
                                variant="toolbar-btn"
                                sub_variant="primary"
                                owner="admin"
                                onClick={() => {
                                  props.onTransitionSettings();
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  owner="admin"
                                  className="btn-lh"
                                >
                                  {width <= 1200 ? (
                                    <SvgIcon
                                      sx={{
                                        width: "24px",
                                        height: "24px",
                                        fill: "currentColor",
                                      }}
                                    >
                                      <svg
                                        viewBox="0 0 447.489 375.571"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M 306.696 161.014 L 202.958 161.014 L 250.207 113.765 L 227.948 91.506 L 153.82 165.634 C 150.872 168.587 149.214 172.591 149.214 176.765 C 149.214 180.938 150.872 184.942 153.82 187.895 L 227.948 262.023 L 250.207 239.764 L 202.958 192.515 L 306.696 192.515 L 306.696 161.014 Z"
                                          style={{
                                            transformBox: "fill-box",
                                            transformOrigin: "50% 50%",
                                          }}
                                          transform="matrix(-1, 0, 0, -1, -0.000024, 0.000005)"
                                        />
                                        <rect
                                          x="359.018"
                                          y="34.817"
                                          width="31.963"
                                          height="283.676"
                                          transform="matrix(1, 0, 0, 1, -293.949796010571, 0)"
                                        />
                                        <rect
                                          x="359.018"
                                          y="34.817"
                                          width="31.963"
                                          height="283.676"
                                        />
                                      </svg>
                                    </SvgIcon>
                                  ) : (
                                    "Transitions"
                                  )}
                                </Typography>
                              </Button>
                            </Tooltip>
                          </Box>
                        </Col>
                      </>
                    )}

                    {presenterAudioEnabled && (
                      <Col xs="auto">
                        <Box className="bx">
                          <Tooltip
                            id="notes-tooltip"
                            title="Slide audio"
                            placement="bottom"
                          >
                            <Button
                              ref={audioTooltipTarget}
                              className={hasSlideAudio ? "has-slide-audio" : ""}
                              id="slide-audio-setting-button"
                              variant="toolbar-btn"
                              sub_variant="primary"
                              owner="admin"
                              onClick={() => {
                                props.onSlideAudio();
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                owner="admin"
                                className="btn-lh"
                              >
                                {width <= 1200 ? (
                                  <RecordVoiceOverIcon
                                    sx={{ width: "20px", height: "20px" }}
                                  />
                                ) : (
                                  "Audio"
                                )}
                              </Typography>
                            </Button>
                          </Tooltip>
                        </Box>
                      </Col>
                    )}
                    {presenterNotesEnabled && (
                      <Col xs="auto">
                        <Box className="bx">
                          <Tooltip
                            id="notes-tooltip"
                            title="Slide notes"
                            placement="bottom"
                          >
                            <Button
                              ref={notesTooltipTarget}
                              variant="toolbar-btn"
                              sub_variant="primary"
                              owner="admin"
                              onClick={() => {
                                props.onSlideNotes();
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                owner="admin"
                                className="btn-lh"
                              >
                                {width <= 1200 ? (
                                  <NotesIcon
                                    sx={{ width: "20px", height: "20px" }}
                                  />
                                ) : (
                                  "Notes"
                                )}
                              </Typography>
                            </Button>
                          </Tooltip>
                        </Box>
                      </Col>
                    )}
                    {!isCorrupt && (
                      <Col xs="auto">
                        <Box className="bx">
                          <Tooltip
                            id="delete-tooltip"
                            title="Delete slide"
                            placement="bottom"
                          >
                            <Button
                              ref={deleteTooltipTarget}
                              variant="toolbar-btn"
                              sub_variant="error"
                              owner="admin"
                              onClick={() => {
                                props.onRemoveSlides([slide.id]);
                              }}
                              startIcon={
                                width < 1400 ? (
                                  ""
                                ) : (
                                  <SvgIcon>
                                    <svg
                                      style={{
                                        fill: "currentColor",
                                        fontSize: "0.8rem",
                                      }}
                                      id="Layer_1"
                                      data-name="Layer 1"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 881.19 1069.98"
                                    >
                                      <path
                                        d="M248,1070a166.75,166.75,0,0,1-118.45-49.28l-.07-.07c-31.82-32-49.35-74-49.35-118.52V410.69H46a46.06,46.06,0,0,1-46-46L.53,256.88A122.12,122.12,0,0,1,36.59,170.2l.19-.18a122.17,122.17,0,0,1,86.65-36H246.87V96a96.14,96.14,0,0,1,96-96h196a96.14,96.14,0,0,1,96,96v38H758.29A122.08,122.08,0,0,1,845,170.08l.46.45a122.56,122.56,0,0,1,35.75,86.39l-.53,107.81a46.07,46.07,0,0,1-46,46H800.52v491.4A168.23,168.23,0,0,1,632.65,1070ZM172.11,902.13a75.12,75.12,0,0,0,22.17,53.52l.44.44A75.15,75.15,0,0,0,248,978H632.63a76.26,76.26,0,0,0,75.86-75.86V410.69H172.11ZM788.66,318.68l.52-62a30.43,30.43,0,0,0-9-21.61l-.35-.34a30.53,30.53,0,0,0-21.59-8.79H123.43a30.57,30.57,0,0,0-21.78,9l-.17.17a30.6,30.6,0,0,0-9,21.69L92,318.67ZM562.84,134V96a24.15,24.15,0,0,0-24-24h-196a24.15,24.15,0,0,0-24,24v38Z"
                                        transform="translate(0 -0.02)"
                                      />
                                      <path
                                        d="M575.05,844a31.1,31.1,0,0,1-31-31V575.68a31,31,0,1,1,62,0V813A31,31,0,0,1,575.05,844Z"
                                        transform="translate(0 -0.02)"
                                      />
                                      <path
                                        d="M295.57,844a31,31,0,0,1-31-31V575.68a31,31,0,0,1,62,0V813A31.09,31.09,0,0,1,295.57,844Z"
                                        transform="translate(0 -0.02)"
                                      />
                                      <path
                                        d="M435.3,844a31,31,0,0,1-31-31V575.68a31,31,0,0,1,62,0V813A31.09,31.09,0,0,1,435.3,844Z"
                                        transform="translate(0 -0.02)"
                                      />
                                    </svg>
                                  </SvgIcon>
                                )
                              }
                            >
                              <Typography
                                variant="subtitle2"
                                owner="admin"
                                className="btn-lh"
                              >
                                {width < 1400 ? (
                                  <SvgIcon>
                                    <svg
                                      style={{
                                        fill: "currentColor",
                                        fontSize: "0.8rem",
                                      }}
                                      id="Layer_1"
                                      data-name="Layer 1"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 881.19 1069.98"
                                    >
                                      <path
                                        d="M248,1070a166.75,166.75,0,0,1-118.45-49.28l-.07-.07c-31.82-32-49.35-74-49.35-118.52V410.69H46a46.06,46.06,0,0,1-46-46L.53,256.88A122.12,122.12,0,0,1,36.59,170.2l.19-.18a122.17,122.17,0,0,1,86.65-36H246.87V96a96.14,96.14,0,0,1,96-96h196a96.14,96.14,0,0,1,96,96v38H758.29A122.08,122.08,0,0,1,845,170.08l.46.45a122.56,122.56,0,0,1,35.75,86.39l-.53,107.81a46.07,46.07,0,0,1-46,46H800.52v491.4A168.23,168.23,0,0,1,632.65,1070ZM172.11,902.13a75.12,75.12,0,0,0,22.17,53.52l.44.44A75.15,75.15,0,0,0,248,978H632.63a76.26,76.26,0,0,0,75.86-75.86V410.69H172.11ZM788.66,318.68l.52-62a30.43,30.43,0,0,0-9-21.61l-.35-.34a30.53,30.53,0,0,0-21.59-8.79H123.43a30.57,30.57,0,0,0-21.78,9l-.17.17a30.6,30.6,0,0,0-9,21.69L92,318.67ZM562.84,134V96a24.15,24.15,0,0,0-24-24h-196a24.15,24.15,0,0,0-24,24v38Z"
                                        transform="translate(0 -0.02)"
                                      />
                                      <path
                                        d="M575.05,844a31.1,31.1,0,0,1-31-31V575.68a31,31,0,1,1,62,0V813A31,31,0,0,1,575.05,844Z"
                                        transform="translate(0 -0.02)"
                                      />
                                      <path
                                        d="M295.57,844a31,31,0,0,1-31-31V575.68a31,31,0,0,1,62,0V813A31.09,31.09,0,0,1,295.57,844Z"
                                        transform="translate(0 -0.02)"
                                      />
                                      <path
                                        d="M435.3,844a31,31,0,0,1-31-31V575.68a31,31,0,0,1,62,0V813A31.09,31.09,0,0,1,435.3,844Z"
                                        transform="translate(0 -0.02)"
                                      />
                                    </svg>
                                  </SvgIcon>
                                ) : (
                                  "Delete"
                                )}
                              </Typography>
                            </Button>
                          </Tooltip>
                        </Box>
                      </Col>
                    )}
                    {!isReadOnly && !isCorrupt && (
                      <Col xs="auto">
                        <Box
                          className={`bx ${width >= 1400 ? "checkbox" : ""}`}
                        >
                          <Tooltip
                            id="slide-visibility-toggle-tooltip"
                            title={
                              slide?.published === true
                                ? "Hide slide"
                                : "Unhide slide"
                            }
                            placement="bottom"
                          >
                            {width >= 1400 ? (
                              <FormControlLabel
                                labelPlacement="end"
                                className="d-flex align-items-center"
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={
                                      slide?.published === true ? false : true
                                    }
                                    sx={{
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: "0.75rem",
                                        fontWeight: 500,
                                        color: "#777",
                                      },
                                    }}
                                    onChange={(event) => {
                                      props.toggleStatus([slide.id]);
                                    }}
                                  />
                                }
                                label={
                                  <Typography
                                    variant="subtitle2"
                                    owner="admin"
                                    color="adminText.dark"
                                  >
                                    Hide
                                  </Typography>
                                }
                              />
                            ) : (
                              <Button
                                ref={deleteTooltipTarget}
                                variant="toolbar-btn"
                                sub_variant="primary"
                                owner="admin"
                                onClick={() => {
                                  props.toggleStatus([slide.id]);
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  owner="admin"
                                  className="btn-lh"
                                >
                                  {slide?.published === true ? (
                                    <UnhideIcon
                                      sx={{ width: "20px", height: "20px" }}
                                    />
                                  ) : (
                                    <HideIcon
                                      sx={{ width: "20px", height: "20px" }}
                                    />
                                  )}
                                </Typography>
                              </Button>
                            )}
                          </Tooltip>
                        </Box>
                      </Col>
                    )}
                  </Row>
                </Col>
              )}
            </Row>
          </>
        )}
      </Container>
    </div>
  );
}
